/**
 * Acknowledge an account alert (US 34815). Tim: "on the accounts that are
 * going to hit a limit I should be able to click acknowledge and not get
 * another alert on that account for 2+ hours."
 *
 * - Keyed on the ACCOUNT (lower-cased UPN), so both its 5h and 7d rows go
 *   quiet together.
 * - No early re-alert when it gets worse: Tim asked for silence, so a
 *   snoozed account stays silent until the snooze ends.
 * - Only Needs attention alerts (and so the Accounts tile count) are
 *   suppressed; the Accounts card still shows the account's state.
 * - A snooze never outlives 2 h from when THIS screen first saw the ack: it
 *   ends at min(stamp, firstSeen) + `ACCOUNT_ACK_SNOOZE_MS`. A future stamp
 *   (a bad FlexData row, a clock ahead of this one) therefore caps at 2 h
 *   from first sight and can never re-activate later, and a past stamp ends
 *   2 h after the click. firstSeen is fixed per stamp, so nothing slides.
 */
import { parseUtcMs } from '../models/flock-telemetry.utils';
import type { Alert } from './attention.logic';

/** How long an acknowledged account stays silent. Raise it here. */
export const ACCOUNT_ACK_SNOOZE_MS = 2 * 60 * 60_000;

/** One acknowledgement: its stamp and when this screen first saw it. */
export interface AccountAck {
  atMs: number;
  seenMs: number;
}

/** Lower-cased account UPN -> its acknowledgement. */
export type AccountAcks = ReadonlyMap<string, AccountAck>;

/** A click on this screen: stamped and seen now. */
export function acknowledgeAccount(
  acks: AccountAcks,
  accountUpn: string,
  atMs: number
): Map<string, AccountAck> {
  const next = new Map(acks);
  next.set(accountUpn.toLowerCase(), { atMs, seenMs: atMs });
  return next;
}

/**
 * Folds the bridge's stamps into what this screen holds. The newest stamp
 * per account wins (an unsynced local click survives an older row); a stamp
 * already held keeps its first-seen time; a new stamp is first seen now.
 */
export function observeAcks(
  current: AccountAcks,
  remote: ReadonlyMap<string, number>,
  nowMs: number
): Map<string, AccountAck> {
  const next = new Map(current);
  for (const [key, atMs] of remote) {
    const held = next.get(key);
    if (held && held.atMs >= atMs) continue;
    next.set(key, { atMs, seenMs: nowMs });
  }
  return next;
}

/** When the account's snooze ends, or null when it is not acknowledged. */
export function snoozedUntilMs(
  accountUpn: string,
  acks: AccountAcks
): number | null {
  const ack = acks.get(accountUpn.toLowerCase());
  return ack ? Math.min(ack.atMs, ack.seenMs) + ACCOUNT_ACK_SNOOZE_MS : null;
}

export function isAccountSnoozed(
  accountUpn: string,
  acks: AccountAcks,
  nowMs: number
): boolean {
  const until = snoozedUntilMs(accountUpn, acks);
  return until !== null && nowMs < until;
}

/** Drops account alerts for snoozed accounts; every other alert passes. */
export function suppressAcknowledged(
  alerts: readonly Alert[],
  acks: AccountAcks,
  nowMs: number
): Alert[] {
  if (acks.size === 0) return [...alerts];
  return alerts.filter(
    (a) =>
      a.kind !== 'account' || !isAccountSnoozed(a.action.target, acks, nowMs)
  );
}

/** Where acks live on the bridge: FlexData (shared across screens). */
export const ACCOUNT_ACK_FLEX = {
  entityType: 'wallboard',
  category: 'account-ack',
} as const;

type FlexRow = Record<string, unknown>;

/** FlexData GET rows -> acks (server updatedAt, else payload ackedAt, also when updatedAt does not parse); rows without a key or any parseable stamp are skipped. */
export function parseAccountAcks(
  rows: readonly FlexRow[]
): Map<string, number> {
  const acks = new Map<string, number>();
  for (const r of rows) {
    const key = r['Key'] ?? r['key'];
    if (typeof key !== 'string' || !key) continue;
    let payload = r['JsonPayload'] ?? r['jsonPayload'];
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        continue;
      }
    }
    // The row's server clock (SYSUTCDATETIME on upsert) is the ack time, so
    // a client's fast clock cannot push a snooze forward; the payload's
    // ackedAt is the fallback for a row without one that parses (Task 34832).
    const serverAt = r['UpdatedAt'] ?? r['updatedAt'];
    const ackedAt =
      payload && typeof payload === 'object'
        ? (payload as { ackedAt?: unknown }).ackedAt
        : null;
    const at = stampMs(serverAt) ?? stampMs(ackedAt);
    if (at === null) continue;
    acks.set(key.toLowerCase(), at);
  }
  return acks;
}

function stampMs(value: unknown): number | null {
  return typeof value === 'string' ? parseUtcMs(value) : null;
}
